import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { 
  BookOpen, Calendar, User, Eye, ArrowLeft, Tag, 
  Loader2, Search, Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import * as api from '../services/api';

const TECH_FONT = "'Inter', 'SF Pro Display', sans-serif";

const BLOG_CATEGORIES = [
  { value: 'all', label: 'Todos' },
  { value: 'opinion', label: 'Opinión' },
  { value: 'tutorial', label: 'Tutoriales' },
  { value: 'analisis', label: 'Análisis' },
  { value: 'tendencias', label: 'Tendencias' },
  { value: 'general', label: 'General' }
];

// Blog List Page
export const BlogListPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const data = await api.getBlogPosts({ status: 'published' });
      setPosts(data || []);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPosts = posts.filter(post => {
    const matchesSearch = searchTerm.trim() === '' || 
      post.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.excerpt?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="blog-list-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0f172a] to-slate-800 text-white py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <BookOpen className="w-8 h-8 text-cyan-400" />
            <h1 className="text-3xl font-bold" style={{ fontFamily: TECH_FONT }}>
              Blog Editorial
            </h1>
          </div>
          <p className="text-gray-300 max-w-2xl">
            Opiniones, análisis y reflexiones de nuestro equipo editorial sobre tecnología y tendencias digitales.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative w-full md:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar posts..."
                className="pl-10"
                data-testid="blog-search"
              />
            </div>

            {/* Categories */}
            <div className="flex gap-2 flex-wrap justify-center">
              {BLOG_CATEGORIES.map(cat => (
                <button
                  key={cat.value}
                  onClick={() => setSelectedCategory(cat.value)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedCategory === cat.value
                      ? 'bg-cyan-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Posts Grid */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {filteredPosts.length === 0 ? (
          <div className="text-center py-20">
            <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-600 mb-2">No hay posts</h3>
            <p className="text-gray-400">
              {searchTerm ? 'No se encontraron posts con ese término' : 'Pronto habrá contenido nuevo'}
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPosts.map(post => (
              <Link
                key={post.blog_id}
                to={`/blog/${post.blog_id}`}
                className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all"
                data-testid={`blog-card-${post.blog_id}`}
              >
                {/* Cover Image */}
                <div className="aspect-video bg-gradient-to-br from-cyan-500 to-blue-600 relative overflow-hidden">
                  {post.cover_image ? (
                    <img 
                      src={post.cover_image} 
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <BookOpen className="w-12 h-12 text-white/50" />
                    </div>
                  )}
                  <Badge className="absolute top-3 left-3 bg-white/90 text-gray-800">
                    {BLOG_CATEGORIES.find(c => c.value === post.category)?.label || post.category}
                  </Badge>
                </div>

                {/* Content */}
                <div className="p-5">
                  <h2 className="font-bold text-lg text-gray-900 mb-2 line-clamp-2 group-hover:text-cyan-600 transition-colors">
                    {post.title}
                  </h2>
                  <p className="text-gray-500 text-sm line-clamp-3 mb-4">
                    {post.excerpt}
                  </p>

                  {/* Meta */}
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <div className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      <span>{post.author_name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        {post.views || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {post.published_at ? new Date(post.published_at).toLocaleDateString('es') : ''}
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Blog Detail Page
export const BlogDetailPage = () => {
  const { blogId } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [blogId]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      const data = await api.getBlogPost(blogId);
      setPost(data);
    } catch (error) {
      console.error('Error fetching blog post:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center">
        <h1 className="text-2xl font-bold text-gray-600 mb-4">Post no encontrado</h1>
        <Link to="/blog">
          <Button>Volver al Blog</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="blog-detail-page">
      {/* Hero */}
      <div className="bg-gradient-to-r from-[#0f172a] to-slate-800 text-white py-8">
        <div className="max-w-4xl mx-auto px-4">
          <Link to="/blog" className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300 mb-6">
            <ArrowLeft className="w-4 h-4" />
            Volver al Blog
          </Link>
          <Badge className="bg-cyan-500 text-white mb-4">
            {BLOG_CATEGORIES.find(c => c.value === post.category)?.label || post.category}
          </Badge>
          <h1 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: TECH_FONT }}>
            {post.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-gray-300 text-sm">
            <span className="flex items-center gap-2">
              <User className="w-4 h-4" />
              {post.author_name}
            </span>
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {post.published_at ? new Date(post.published_at).toLocaleDateString('es', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }) : ''}
            </span>
            <span className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              {post.views || 0} vistas
            </span>
          </div>
        </div>
      </div>

      {/* Cover Image */}
      {post.cover_image && (
        <div className="max-w-4xl mx-auto px-4 -mt-4">
          <img 
            src={post.cover_image} 
            alt={post.title}
            className="w-full rounded-xl shadow-lg"
          />
        </div>
      )}

      {/* Content */}
      <article className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-sm p-8 prose prose-lg max-w-none">
          <div dangerouslySetInnerHTML={{ __html: post.content.replace(/\n/g, '<br />') }} />
        </div>

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="mt-6 flex flex-wrap gap-2">
            {post.tags.map((tag, idx) => (
              <Badge key={idx} variant="outline" className="flex items-center gap-1">
                <Tag className="w-3 h-3" />
                {tag}
              </Badge>
            ))}
          </div>
        )}
      </article>
    </div>
  );
};

export default BlogListPage;
